import React from 'react';
import { getAccentColor } from '../lib/jlfcTheme';
import { Stack, PasswordInput, Group, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import JlfcModal, { blockModalEnterKey } from './JlfcModal';
import { CancelButton, SaveButton } from './FormActionButtons';
import { authFetch, parseJsonResponse } from '../lib/api';
import { confirmAbandon } from '../lib/formAbandon';

export default function ChangePasswordModal({ opened, onClose, onChanged }) {
  const form = useForm({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      currentPassword: (v) => (v ? null : 'Mot de passe actuel requis'),
      newPassword: (v) => {
        if (!v) return 'Nouveau mot de passe requis';
        if (v.length < 4) return '4 caractères minimum';
        return null;
      },
      confirmPassword: (v, values) => {
        if (!v) return 'Confirmation requise';
        if (v !== values.newPassword) return 'Les mots de passe ne correspondent pas';
        return null;
      },
    },
  });

  const handleClose = async () => {
    if (!(await confirmAbandon(form.isDirty()))) return;
    form.reset();
    form.clearErrors();
    onClose();
  };

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      const res = await authFetch('/api/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok || !data.success) {
        notifications.show({
          title: 'Erreur',
          message: data.message || 'Modification impossible',
          color: 'red',
        });
        return;
      }
      notifications.show({
        title: 'Mot de passe modifié',
        message: data.message || 'Reconnectez-vous avec votre nouveau mot de passe.',
        color: getAccentColor(),
      });
      handleClose();
      onChanged?.();
    } catch (err) {
      console.error('[ChangePasswordModal]', err);
      notifications.show({
        title: 'Erreur',
        message: err?.message || 'Erreur réseau',
        color: 'red',
      });
    }
  });

  return (
    <JlfcModal opened={opened} onClose={handleClose} title="Changer mon mot de passe" size="sm">
      <form onSubmit={handleSubmit} onKeyDown={blockModalEnterKey}>
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Après validation, vous serez déconnecté et devrez vous reconnecter avec le nouveau mot de
            passe.
          </Text>
          <PasswordInput
            label="Mot de passe actuel"
            autoComplete="current-password"
            value={form.values.currentPassword}
            onChange={(e) => form.setFieldValue('currentPassword', e.currentTarget.value)}
            onBlur={() => form.validateField('currentPassword')}
            error={form.errors.currentPassword}
          />
          <PasswordInput
            label="Nouveau mot de passe"
            autoComplete="new-password"
            value={form.values.newPassword}
            onChange={(e) => form.setFieldValue('newPassword', e.currentTarget.value)}
            onBlur={() => form.validateField('newPassword')}
            error={form.errors.newPassword}
          />
          <PasswordInput
            label="Confirmer le nouveau mot de passe"
            autoComplete="new-password"
            value={form.values.confirmPassword}
            onChange={(e) => form.setFieldValue('confirmPassword', e.currentTarget.value)}
            onBlur={() => form.validateField('confirmPassword')}
            error={form.errors.confirmPassword}
          />
          <Group justify="flex-end" mt="md">
            <CancelButton onClick={handleClose} />
            <SaveButton />
          </Group>
        </Stack>
      </form>
    </JlfcModal>
  );
}
